<template>
  <v-flex xs12>
    <v-card class="px-2 py-3">
      <v-layout row wrap justify-space-between>
        <h1 class="px-3 pb-3">
          {{ isEdit ? `${$lang.actions.edit} ${company.name}`
          : $lang.labels.createCompany}}
        </h1>
        <div class="d-inline-flex" v-if="!isEdit && !isLocalAdmin">
        <v-autocomplete
          v-if="showCompanies"
          class="min-w max-w mx-3 pb-2"
          :label="$lang.actions.selectCompany"
          :items="mappedCompanies"
          v-model="selectedCompany"
          hide-details
        />
        <div class="pr-3"><v-btn @click="fetchAllCompanies()" class="primary mx-0 mb-4">{{$lang.actions.importCompany}}</v-btn></div>
        </div>
      </v-layout>

      <v-flex px-3 pt-2 pb-4>
        <hr>
      </v-flex>

      <v-form v-model="valid">
        <v-layout row wrap justify-space-between>
          <v-flex xs12>
          <h2 class="px-3 pb-3">
            {{$lang.header.companyGeneralData}}
          </h2>
          </v-flex>
          <v-flex xs12 sm6 md4 px-3>
            <v-text-field
                :label="$lang.labels.name"
                :placeholder="$lang.placeholders.companyName"
                :rules="[v => !!v || `${$lang.errors.missingCompanyName}`]"
                type="text"
                id="name"
                v-model="company.name"
                required
                :hint="company.name.length > 12 ? 'Name will be shortened on webshop in mobile view' : ''"
                persistent-hint
                class="required" />
            <v-text-field
                :label="$lang.labels.email"
                :placeholder="$lang.placeholders.companyEmail"
                type="email"
                id="email"
                required
                class="required"
                v-model="company.email"
                :rules="emailRules" />
          </v-flex>
          <v-flex xs12 sm6 md4 px-3>
            <v-text-field
                :label="$lang.labels.registrationNumber"
                type="text"
                id="registrationNumber"
                v-model="company.registrationNumber" />
            <v-text-field
                :label="$lang.labels.phone"
                :placeholder="$lang.placeholders.phone"
                :rules="[v => !!v || `${$lang.errors.missingCompanyPhone}`]"
                required
                class="required"
                type="text"
                id="phone"
                v-model="company.phone" />
          </v-flex>
          <v-flex xs12 sm6 md4 px-3>
            <v-text-field
                :label="$lang.labels.registrationLocation"
                type="text"
                id="registrationLocation"
                v-model="company.registrationLocation" />
            <v-select
                :rules="[company.cuisines.length !== 0 || `${$lang.errors.missingCuisines}`]"
                required
                class="required"
                multiple
                small-chips
                deletable-chips
                :items="cuisines"
                v-model="company.cuisines"
                :label="$lang.labels.cuisines"
            />
          </v-flex>
        </v-layout>

        <v-flex px-3 pt-2 pb-4>
          <hr>
        </v-flex>

        <v-layout row wrap justify-space-between>
          <v-flex xs12>
            <h2 class="px-3 pb-3">
              {{$lang.header.companySettings}}
            </h2>
          </v-flex>
          <v-flex xs12 sm6 md4 px-3>
            <v-layout row wrap justify-space-between>
              <v-flex xs12 md6 class="currency-wrap">
                <v-select
                    :label="$lang.labels.currency"
                    :items="currencies"
                    name="currency"
                    id="currency"
                    v-model="company.currency"
                    :rules="[v => !!v || `${$lang.errors.missingCurrency}`]"
                    required
                    class="required pb-2 mb-1" />
              </v-flex>
              <v-flex xs12 md6 class="radio-input" style="min-width:180px;">
                <p>{{$lang.labels.align}}:</p>
                <v-radio-group v-model="align" row hide-details>
                  <v-radio :label="$lang.labels.left" value="left" color="primary"/>
                  <v-radio :label="$lang.labels.right" value="right" color="primary"/>
                </v-radio-group>
              </v-flex>
            </v-layout>
            <v-select
                :label="$lang.labels.defLang"
                :items="languages"
                v-model="company.defaultLanguage"
                item-text="text"
                item-value="value"
                required
                class="required"
                :rules="[v => !!v || `${$lang.errors.missingLang}`]"
            />
          </v-flex>
          <v-flex xs12 sm6 md4 px-3>
            <v-layout row wrap>
              <v-flex xs12 sm6 pr-2>
                <v-select
                  :label="$lang.labels.distanceUnit"
                  :placeholder="$lang.placeholders.distanceUnit"
                  :items="distanceUnits"
                  v-model="company.distanceUnit"
                  :disabled="distanceUnits.length <= 0"
                  required
                  class="required pb-2 mb-1"
                  :rules="[v => !!v || `${$lang.errors.missingdistanceUnit}`]"
                />
              </v-flex>
              <v-flex xs12 sm6 pl-2>
                <v-autocomplete
                  :label="$lang.labels.timezone"
                  :placeholder="$lang.labels.timezone"
                  :items="timezones"
                  v-model="company.timezone"
                  required
                  class="required"
                  :rules="[v => !!v || `${$lang.errors.missingTimezone}`]"
                />
              </v-flex>
            </v-layout>
            <v-text-field
                :label="$lang.labels.bookKeeperEmails"
                type="text"
                id="bookKeeperEmails"
                v-model="bookKeeperEmails" />
          </v-flex>
          <v-flex xs12 sm6 md4 px-3>
            <v-select
                :label="$lang.labels.pricingGroups"
                :placeholder="$lang.placeholders.pricingGroups"
                multiple
                chips
                deletable-chips
                :items="mappedGroups"
                v-model="company.pricingGroups"
                :disabled="mappedGroups.length <= 0"
                required
                class="required"
                :rules="[company.pricingGroups.length > 0 || `${$lang.errors.missingPricingGroups}`]"
            />
            <v-text-field
                :label="$lang.labels.fetchConf"
                v-model="company.config_update_frequency_in_minutes"
                :rules="[v => company.config_update_frequency_in_minutes.toString().length > 0 || `${$lang.errors.missingFetchConf}`]"
                required
                class="required"
                type="number"
                min="0"
            />
          </v-flex>
          <v-flex xs12>
          <p class="px-3 pb-3 id-for-s3" v-if="company.companyRandomId">{{$lang.labels.idForS3}}: {{company.companyRandomId}}</p>
          </v-flex>
          <v-flex xs12 sm6 md4 px-3>
            <v-flex xs12 sm6 md4 pb-2>
              <v-checkbox
                  v-model="company.test"
                  class="option"
                  hide-details
                  color="primary"
                  :label="$lang.labels.testMode"
              />
              <v-checkbox
                v-model="company.dailyOrdersCounterEnabled"
                class="option"
                hide-details
                color="primary"
                :label="$lang.labels.dailyOrdersCounterEnabled"
              />
              <v-checkbox
                  v-model="company.enableReservationForm"
                  class="option"
                  hide-details
                  color="primary"
                  :label="$lang.labels.enquiryReser"
              />
            </v-flex>
          </v-flex>
          <v-flex xs12 sm6 md4 px-3>
            <v-select
              :label="$lang.labels.payIntegModule"
              :placeholder="$lang.placeholders.payIntegModule"
              :items="paymentGateways"
              v-model="company.paymentIntegration"
              :disabled="paymentGateways.length <= 0"
            />
            <v-checkbox
                v-model="company.enableContactForm"
                class="option"
                hide-details
                color="primary"
                :label="$lang.labels.enquiryContact"
            />
            <v-text-field
                v-if="company.enableReservationForm"
                type="number"
                min="1"
                required
                class="required"
                :rules="[v => !!v || `${$lang.errors.missingMinPersons}`]"
                :label="$lang.labels.minPersons"
                v-model="company.minPersons"
            />
          </v-flex>
          <v-flex xs12 sm6 md4 px-3>
            <v-checkbox
              v-model="company.completePrint"
              class="option"
              hide-details
              color="primary"
              :label="$lang.labels.orderPrint"
            />
            <v-checkbox
              v-model="company.persistKitchenPrint"
              class="option"
              hide-details
              color="primary"
              :label="$lang.labels.enableKitchenPrintPersistence"
            />
            <v-text-field
                v-if="company.enableReservationForm"
                type="number"
                min="1"
                required
                class="required"
                :rules="[v => !!v || `${$lang.errors.missingMaxPersons}`]"
                :label="$lang.labels.maxPersons"
                v-model="company.maxPersons"
            />
          </v-flex>
        </v-layout>
        <v-layout row wrap pt-2>
          <v-flex xs12 sm6 md4 px-3 v-if="isEdit">
            <v-text-field
              :label="$lang.labels.swithcCode"
              v-model="company.tableSwitchCode"
              type="number"
              min="0"
              max="9999"
              mask="####"
              :hint="$lang.labels.swithcCodeHint"
            />
          </v-flex>
          <v-flex xs12 sm6 md4 px-3>
            <v-text-field
              :label="$lang.labels.tableIdleTimeInMinutes"
              v-model="company.tableIdleTimeInMinutes"
              type="number"
              min="0"
              max="120"
              required
              class="required"
              :rules="[v => (company.tableIdleTimeInMinutes !== null && company.tableIdleTimeInMinutes.toString().length > 0) ||
              `${$lang.errors.missingTableMin}`]"
            />
          </v-flex>
        </v-layout>
        <v-flex xs12 px-3>
          <v-autocomplete
            :label="$lang.labels.paymentTypes"
            :placeholder="$lang.placeholders.paymentTypes"
            multiple
            chips
            deletable-chips
            :items="payments"
            v-model="company.paymentTypes"
            required
            class="required"
            :rules="[company.paymentTypes.length > 0 || `${$lang.errors.missingPayments}`]"
          />
        </v-flex>

        <v-flex px-3 pt-2 pb-4>
          <hr>
        </v-flex>

        <v-layout row wrap justify-space-between>
          <v-flex xs12>
            <h2 class="px-3 pb-3">
              {{$lang.header.companyAddress}}
            </h2>
          </v-flex>
          <v-flex xs12 sm6 md4 px-3>
            <v-select
                :label="$lang.labels.country"
                :placeholder="$lang.placeholders.companyCountry"
                :rules="[v => !!v || `${$lang.errors.missingCompanyCountry}`]"
                :items="countries"
                type="text"
                id="country"
                v-model="company.address.country"
                required
                class="required"
            />
            <v-text-field
                :label="$lang.labels.postalcode"
                :placeholder="$lang.placeholders.companyPostal"
                :rules="[v => !!v || `${$lang.errors.missingCompanyPostal}`]"
                type="text"
                id="postalcode"
                v-model="company.address.postalCode"
                required
                class="required" />
            <v-text-field
                :label="$lang.labels.townCity"
                :placeholder="$lang.placeholders.companyTown"
                :rules="[v => !!v || `${$lang.errors.missingCompanyTown}`]"
                type="text"
                id="town"
                v-model="company.address.townCity"
                required
                class="required"
            />
            <v-text-field
                :label="$lang.labels.locality"
                :placeholder="$lang.placeholders.companyLocality"
                type="text"
                id="locality"
                v-model="company.address.locality"
            />
          </v-flex>
          <v-flex xs12 sm6 md4 px-3>
            <v-btn @click="addressDialog = true" :disabled="company.address.country === ''" class="primary mx-0 mb-4">{{$lang.labels.addressLookup}}</v-btn>
            <v-text-field
                :label="$lang.labels.latitude"
                :rules="latitudeRule"
                type="text"
                v-model="company.address.latitude"
                required
                class="required"
            />
            <v-text-field
                :label="$lang.labels.longitude"
                :rules="latitudeRule"
                type="text"
                v-model="company.address.longitude"
                class="required"
            />
          </v-flex>
          <v-flex xs12 sm6 md4 px-3>
            <v-text-field
                :label="$lang.labels.addressLine + ' 1'"
                :rules="[v => !!v || `${$lang.errors.missingCompanyAddress}`]"
                type="text"
                id="address1"
                v-model="company.address.line1"
                required
                class="required" />
            <v-text-field
                :label="$lang.labels.addressLine + ' 2'"
                type="text"
                id="address2"
                v-model="company.address.line2" />
            <v-text-field
                :label="$lang.labels.addressLine + ' 3'"
                type="text"
                id="address3"
                v-model="company.address.line3" />
            <v-text-field
                :label="$lang.labels.addressLine + ' 4'"
                type="text"
                id="address4"
                v-model="company.address.line4" />
          </v-flex>
        </v-layout>

        <v-flex px-3 pt-2 pb-4>
          <hr>
        </v-flex>

        <v-flex xs12 px-3>
          <v-btn @click="addHours" :disabled="editingHours" class="primary mx-0">{{$lang.actions.openingHours}}</v-btn>
          <transition name="fade">
            <div class="v-table__overflow custom-table">
              <table class="v-datatable v-table theme--light">
                <thead v-if="openingTimes.length !== 0">
                  <tr>
                    <td class="move-row"/>
                    <th class="text-xs-left"><div class="th-adj">{{$lang.header.day}}<span class="error--text">*</span></div></th>
                    <th class="text-xs-left"><div class="th-adj">{{$lang.header.start}}<span class="error--text">*</span></div></th>
                    <th class="text-xs-left"><div class="th-adj">{{$lang.header.end}}<span class="error--text">*</span></div></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(time, index) in openingTimes"
                    :key="index">
                    <template v-if="time.isEdit">
                      <td class="move-row"/>
                      <td>
                        <v-select
                          class="min-w"
                          :items="days"
                          v-model="tempOpening.day"
                        />
                      </td>
                      <td>
                        <v-text-field
                          v-model="tempOpening.start"
                          type="text"
                          mask="##:##"
                          placeholder="HH:mm"
                          required
                          class="required"
                          :rules="timeRule" />
                      </td>
                      <td>
                        <v-text-field
                          type="text"
                          v-model="tempOpening.end"
                          mask="##:##"
                          placeholder="HH:mm"
                          required
                          class="required"
                          :rules="timeRule"
                        />
                      </td>
                      <td style="min-width: 314px;" class="px-0">
                        <v-btn
                          class="primary elevation-1 mx-0"
                          :disabled="!openingValidation"
                          @click="saveOpening(time)">
                          {{$lang.actions.save}}
                        </v-btn>
                        <v-btn
                          class="primary elevation-1"
                          :disabled="!openingValidation"
                          @click="copyOpening(time)">
                          {{$lang.actions.copy}}
                        </v-btn>
                        <v-btn class="error mx-0 elevation-1" @click="closeOpening(time)">
                          {{$lang.actions.cancel}}
                        </v-btn>
                      </td>
                    </template>
                    <template v-else>
                       <td class="move-row">
                        <div v-if="openingTimes.length >= 2">
                          <v-icon @click="moveUp(openingTimes, index)" v-if="index !== 0">keyboard_arrow_up</v-icon>
                          <v-icon @click="moveDown(openingTimes, index)" v-if="index != openingTimes.length - 1">keyboard_arrow_down</v-icon>
                        </div>
                      </td>
                      <td>{{ time.day }}</td>
                      <td>{{ time.start }}</td>
                      <td>{{ time.end }}</td>
                      <td style="min-width: 314px;" class="px-0">
                        <v-btn class="primary elevation-1 mx-0" @click="editOpening(time)" :disabled="editingHours">
                          {{$lang.actions.edit}}
                        </v-btn>
                        <v-btn
                          class="primary elevation-1"
                          @click="copyOpening(time)">
                          {{$lang.actions.copy}}
                        </v-btn>
                        <v-btn class="error mx-0 elevation-1" @click="deleteOpening(index)">
                          {{$lang.actions.delete}}
                        </v-btn>
                      </td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>
          </transition>
        </v-flex>
        <v-flex xs12 px-3 pb-2 mt-3>
          <v-layout row align-center>
            <v-btn
              :disabled="editingVat || noTax"
              class="primary ml-0 mr-3"
              @click="addVat" >
              {{$lang.actions.addVat}}
            </v-btn>
            <v-checkbox
              v-model="noTax"
              :label="$lang.labels.noVATtax"
              hide-details
              color="primary"
            />
          </v-layout>
          <transition name="fade">
            <div class="v-table__overflow custom-table">
              <table class="v-datatable v-table theme--light">
                <tbody>
                  <tr
                    v-for="(vat, index) in vatRates"
                    :key="index">
                    <template v-if="vat.isEdit">
                      <td>
                        <v-text-field
                          type="number"
                          min="0"
                          step="0.01"
                          class="min-w mb-2 required"
                          required
                          hide-details
                          v-model="tempVat.vat"
                          :label="$lang.actions.addVat" />
                      </td>
                      <td class="min-w-lg px-0">
                        <v-btn class="primary elevation-1" :disabled="!tempVat.vat" @click="saveVatEdit(vat)">{{$lang.actions.save}}</v-btn>
                        <v-btn class="error ml-0 elevation-1" @click="closeVatEdit(vat)">{{$lang.actions.cancel}}</v-btn>
                      </td>
                    </template>
                    <template v-else>
                      <td>{{ vat.vat }}</td>
                      <td class="actions px-0">
                        <v-btn class="primary elevation-1" @click="editVat(vat)">{{$lang.actions.edit}}</v-btn>
                        <v-btn class="error ml-0 elevation-1" @click="deleteVat(index)">{{$lang.actions.delete}}</v-btn>
                      </td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>
          </transition>
          <v-layout row wrap pt-2>
            <v-flex xs12 sm6 px-1>
              <v-layout row align-center>
                <v-flex grow>
                  <v-select
                    :label="$lang.labels.tipTax"
                    :items="vatRates"
                    type="number"
                    step="0.5" min="0"
                    v-model="company.tipTaxRate"
                    :disabled="noTipTax"
                    item-text="vat"
                    item-value="vat"
                  />
                </v-flex>
                <v-flex shrink>
                  <v-checkbox
                    v-model="noTipTax"
                    :label="$lang.labels.noTipTax"
                    hide-details
                    :disabled="this.vatRates.length === 0"
                    class="ml-3"
                    color="primary"
                  />
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex xs12 sm6 px-1>
              <v-layout row align-center>
                <v-flex grow>
                  <v-select
                    :label="$lang.labels.chargeTax"
                    :items="vatRates"
                    v-model="company.deliveryChargesTaxRate"
                    :disabled="noDeliveryTax"
                    item-text="vat"
                    item-value="vat"
                  />
                </v-flex>
                <v-flex shrink>
                  <v-checkbox
                    v-model="noDeliveryTax"
                    :label="$lang.labels.noDeliveryTax"
                    hide-details
                    :disabled="this.vatRates.length === 0"
                    class="ml-3"
                    color="primary"
                  />
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-flex>

        <v-layout row wrap justify-space-between align-center>
          <v-flex xs12 sm6 md4 px-3 >
            <v-select
              :label="$lang.actions.serviceType"
              :items="serviceTypes"
              v-model="company.serviceCharges.type"
              item-text="text"
              item-value="value"
            />
          </v-flex>
          <template v-if="company.serviceCharges.type === 'percoftotal'">
            <v-flex xs12 sm6 md4 px-2>
              <v-layout row align-center>
                <v-flex grow>
                  <v-select
                    :label="$lang.labels.serviceTaxRate"
                    :items="vatRates"
                    v-model="company.serviceCharges.taxRate"
                    :disabled="noServiceTax"
                    item-text="vat"
                    item-value="vat"
                  />
                </v-flex>
                <v-flex shrink>
                  <v-checkbox
                    v-model="noServiceTax"
                    :disabled="vatRates.length === 0"
                    :label="$lang.labels.noServiceTax"
                    hide-details
                    class="ml-3"
                    color="primary"
                  />
                </v-flex>
              </v-layout>
            </v-flex>
            <v-flex xs12 sm6 md4 px-2>
              <v-text-field
                type="number"
                min="0"
                max="100"
                :label="$lang.labels.percOfTotal"
                v-model="company.serviceCharges.percOfTotal"
              />
            </v-flex>
          </template>
        </v-layout>

        <v-flex px-3 pt-2 pb-4>
          <hr>
        </v-flex>

        <v-layout row wrap>
          <v-flex xs12>
            <h2 class="px-3 pb-3">
              {{$lang.header.delivery}}
            </h2>
          </v-flex>
          <v-flex xs12 sm4 px-3>
            <v-text-field
              :label="`${$lang.labels.maxDist} (${company.distanceUnit})`"
              v-model="company.maxDeliveryDistance"
              required
              class="required"
              type="number"
              step="0.5" min="0"
              :rules="distanceRule"
            />
          </v-flex>
        </v-layout>
        <v-flex xs12 px-3>
          <v-textarea
            :label="$lang.labels.delPostal"
            v-model="company.deliveryPostalCodes"
            auto-grow
            rows="1"
          />
        </v-flex>

        <v-layout row wrap>
          <v-flex xs12 sm6 md4 px-3>
            <v-select
              :label="$lang.labels.chargeType"
              :items="chargeTypesFormatted"
              v-model="company.deliveryCharges.type"
              item-text="text"
              item-value="value"
              required
              class="required"
              :rules="[v => !!v || `${$lang.errors.missingChargeType}`]"
            />
          </v-flex>
          <transition name="fade">
            <v-flex xs12 sm6 md4 px-3 v-if="company.deliveryCharges.type === 'chargespermile'">
              <v-text-field
                :label="company.distanceUnit === 'meters' ? $lang.labels.delChargePerMeter : $lang.labels.delChargePerMile"
                v-model="company.deliveryCharges.chargesPerMile"
                required
                class="required"
                step="0.5" min="0"
                :rules="[v => !!v || `${$lang.errors.missingChargePerMile}`]"
                type="number" />
            </v-flex>
          </transition>
          <transition name="fade">
            <v-flex xs12 px-3 v-if="company.deliveryCharges.type === 'chargesbymiles'" pb-4>
              <v-btn @click="addMiles" :disabled="editingMiles" class="primary mx-0">{{ company.distanceUnit === 'meters' ? $lang.actions.chargebyMeters: $lang.actions.chargebyMiles}}</v-btn>
              <div class="v-table__overflow custom-table">
                <table class="v-datatable v-table theme--light">
                  <thead>
                  <tr>
                    <th class="text-xs-left"><div class="th-adj">{{company.distanceUnit === 'meters' ? $lang.header.fromMeters : $lang.header.fromMiles}}<span class="error--text">*</span></div></th>
                    <th class="text-xs-left"><div class="th-adj">{{company.distanceUnit === 'meters' ? $lang.header.toMeters : $lang.header.toMiles}}<span class="error--text">*</span></div></th>
                    <th class="text-xs-left"><div class="th-adj">{{$lang.header.charges}}<span class="error--text">*</span></div></th>
                    <th>
                      <v-text-field
                        required
                        class="required"
                        style="max-width: 250px;"
                        type="number"
                        min="0"
                        steps="0.1"
                        :label="$lang.header.defaultCharges"
                        v-model="defaultCharges"
                        hide-details
                      />
                    </th>
                  </tr>
                  </thead>
                  <tbody v-if="miles.length !== 0">
                  <tr
                    v-for="(charge, index) in miles"
                    :key="index">
                    <template v-if="charge.isEdit">
                      <td>
                        <v-text-field
                          type="number"
                          min="0"
                          step="0.5"
                          v-model="tempMiles.fromMiles"
                        />
                      </td>
                      <td>
                        <v-text-field
                          type="number"
                          min="0"
                          step="0.5"
                          v-model="tempMiles.toMiles"
                        />
                      </td>
                      <td>
                        <v-text-field
                          type="number"
                          min="0"
                          step="0.5"
                          v-model="tempMiles.charges"
                        />
                      </td>
                      <td class="min-w-lg px-0">
                        <v-btn
                          class="primary elevation-1"
                          :disabled="!tempMiles.fromMiles || !tempMiles.toMiles || !tempMiles.charges"
                          @click="saveMilesEdit(charge)">{{$lang.actions.save}}</v-btn>
                        <v-btn class="error ml-0 elevation-1" @click="closeMilesEdit(charge)">{{$lang.actions.cancel}}</v-btn>
                      </td>
                    </template>
                    <template v-else>
                      <td>{{ charge.fromMiles }}</td>
                      <td>{{ charge.toMiles }}</td>
                      <td>{{ charge.charges }}</td>
                      <td class="actions px-0">
                        <v-btn :disabled="editingMiles" class="primary elevation-1" @click="editMiles(charge)">{{$lang.actions.edit}}</v-btn>
                        <v-btn class="error ml-0 elevation-1" @click="deleteMiles(index)">{{$lang.actions.delete}}</v-btn>
                      </td>
                    </template>
                  </tr>
                  </tbody>
                </table>
              </div>
            </v-flex>
          </transition>
          <transition name="fade">
            <v-flex xs12 px-3 v-if="company.deliveryCharges.type === 'chargesbyinvoiceamount'" pb-4>
              <v-btn @click="addAmount" :disabled="editingAmount" class="primary mx-0">{{$lang.actions.chargebyAmount}}</v-btn>
              <div class="v-table__overflow custom-table">
                <table class="v-datatable v-table theme--light">
                  <thead>
                  <tr>
                    <th class="text-xs-left"><div class="th-adj">{{$lang.header.fromAmount}}<span class="error--text">*</span></div></th>
                    <th class="text-xs-left"><div class="th-adj">{{$lang.header.toAmount}}<span class="error--text">*</span></div></th>
                    <th class="text-xs-left"><div class="th-adj">{{$lang.header.charges}}<span class="error--text">*</span></div></th>
                    <th>
                      <v-text-field
                        required
                        class="required"
                        style="max-width: 250px;"
                        type="number"
                        min="0"
                        steps="0.1"
                        :label="$lang.header.defaultCharges"
                        v-model="defaultCharges"
                        hide-details
                      />
                    </th>
                  </tr>
                  </thead>
                  <tbody v-if="amounts.length !== 0">
                  <tr v-for="(amount, index) in amounts" :key="index">
                    <template v-if="amount.isEdit">
                      <td>
                        <v-text-field
                          type="number"
                          min="0"
                          step="0.5"
                          v-model="tempAmount.fromAmount"
                        />
                      </td>
                      <td>
                        <v-text-field
                          type="number"
                          min="0"
                          step="0.5"
                          v-model="tempAmount.toAmount"
                        />
                      </td>
                      <td>
                        <v-text-field
                          type="number"
                          min="0"
                          step="0.5"
                          v-model="tempAmount.charges"
                        />
                      </td>
                      <td class="min-w-lg px-0">
                        <v-btn
                          class="primary elevation-1"
                          :disabled="!tempAmount.fromAmount || !tempAmount.toAmount || !tempAmount.charges"
                          @click="saveAmountEdit(amount)">{{$lang.actions.save}}</v-btn>
                        <v-btn class="error ml-0 elevation-1" @click="closeAmountEdit(amount)">{{$lang.actions.cancel}}</v-btn>
                      </td>
                    </template>
                    <template v-else>
                      <td>{{ amount.fromAmount }}</td>
                      <td>{{ amount.toAmount }}</td>
                      <td>{{ amount.charges }}</td>
                      <td class="actions px-0">
                        <v-btn class="primary elevation-1" @click="editAmount(amount)">{{$lang.actions.edit}}</v-btn>
                        <v-btn class="error ml-0 elevation-1" @click="deleteAmount(index)">{{$lang.actions.delete}}</v-btn>
                      </td>
                    </template>
                  </tr>
                  </tbody>
                </table>
              </div>
            </v-flex>
          </transition>
        </v-layout>

        <v-flex
          px-3 pt-2 pb-4>
          <hr>
        </v-flex>

        <v-flex xs12>
          <h2 class="px-3 pb-3">
            {{$lang.header.automatedPrinting}}
          </h2>
        </v-flex>
        <v-flex xs12>
          <v-data-table
            :headers="headers"
            :items="printingConfigurations"
            :rows-per-page-items="[25, 50, 100, 200, { text:'All' , value: -1 }]">
            <template slot="items" slot-scope="props">
              <td class="min-w">{{props.item.sourceSystem}}</td>
              <td>{{props.item.orderType}}</td>
              <td>{{props.item.printType}}</td>
              <td style="width: 40px;">
                <v-checkbox
                  :disabled="company.address.country === 'AT' && props.item.printType === 'invoice'"
                  v-model="props.item.autoPrinting"
                  color="primary"
                  hide-details
                />
              </td>
            </template>
          </v-data-table>
        </v-flex>

        <v-flex
          v-if="isEdit"
          px-3 pt-2 pb-4>
          <hr>
        </v-flex>

        <v-layout row wrap v-if="isEdit">
          <v-flex xs12>
            <h2 class="px-3 pb-3">
              {{$lang.header.keys}}
            </h2>
          </v-flex>
          <v-flex
            v-if="company.contactServerSettings"
            xs12 md6 px-3>
            <v-textarea
              v-model="company.contactServerSettings.key"
              :label="$lang.labels.contactServerKey"
              color="primary"
              auto-grow
              box
              rows="2"
            />
          </v-flex>
          <v-flex
            v-if="company.walletServerSettings"
            xs12 md6 px-3>
            <v-textarea
              v-model="company.walletServerSettings.key"
              :label="$lang.labels.walletServerKey"
              color="primary"
              auto-grow
              box
              rows="2"
            />
          </v-flex>
          <v-flex
            v-if="company.addressServerSettings"
            xs12 md6 px-3>
            <v-textarea
              v-model="company.addressServerSettings.key"
              :label="$lang.labels.addressServerKey"
              color="primary"
              auto-grow
              box
              rows="2"
            />
          </v-flex>
          <v-flex
            v-if="company.inventoryServerSettings"
            xs12 md6 px-3>
            <v-textarea
              v-model="company.inventoryServerSettings.key"
              :label="$lang.labels.inventoryServerKey"
              color="primary"
              auto-grow
              box
              rows="2"
            />
          </v-flex>
          <v-flex
            v-if="company.reservationServerSettings"
            xs12 md6 px-3>
            <v-textarea
              v-model="company.reservationServerSettings.key"
              :label="$lang.labels.reservationServerSettings"
              color="primary"
              auto-grow
              box
              rows="2"
            />
          </v-flex>
          <v-layout row align-center>
            <v-flex
              xs12 md3
              px-3 shrink>
              <v-checkbox
                v-model="company.isDEPEnabled"
                :label="$lang.labels.enableDEP"
                :readonly="company.address.country === 'UK'"
                color="primary"
              />
            </v-flex>
            <v-flex
              v-if="company.isDEPEnabled"
              xs12 md9 px-3>
              <v-textarea
                v-model="company.depServerSettings.key"
                :label="$lang.labels.depServerKey"
                color="primary"
                auto-grow
                box
                rows="2"
              />
            </v-flex>
          </v-layout>
        </v-layout>

        <v-flex
          v-if="isEdit"
          px-3 pt-2 pb-4>
          <hr>
        </v-flex>

        <v-flex xs12 v-if="err" px-3>
          <v-alert :value="true" type="error">{{err}}</v-alert>
        </v-flex>
        <v-flex xs12 v-if="success" px-3>
          <v-alert :value="true" type="success">{{success}}</v-alert>
        </v-flex>
        <v-layout row wrap class="px-2">
          <v-flex xs12 sm6 md4 px-3>
            <v-text-field
              v-if="isEdit && createdOn"
              :label="$lang.labels.created"
              v-model="createdOn"
              readonly
            />
          </v-flex>
          <v-flex xs12 sm6 md4 px-3>
            <v-text-field
              v-if="isEdit && modifiedOn"
              :label="$lang.labels.modified"
              v-model="modifiedOn"
              readonly
            />
          </v-flex>
        </v-layout>
        <v-layout justify-space-between row align-center class="px-3">
          <v-btn class="mx-0" flat @click="$router.push({ name: 'companies' })">
            <v-icon dark>keyboard_arrow_left</v-icon>
            {{$lang.actions.cancel}}
          </v-btn>
          <div>
            <v-btn v-if="isEdit" type="submit" color="primary" @click.prevent="validate">
              {{$lang.actions.checkValidation}}
            </v-btn>
            <v-btn v-if="isEdit" type="submit" color="primary" :disabled="!valid" @click.prevent="submit('pushConfig')">
              {{$lang.actions.pushConfUpdate}}
            </v-btn>
            <v-btn type="submit" color="primary" :disabled="!valid" class="mx-0" @click.prevent="submit">
              {{ isEdit ? $lang.actions.update : $lang.actions.create}}
            </v-btn>
          </div>
        </v-layout>
      </v-form>
    </v-card>
    <v-snackbar
      v-model="snackbar"
      bottom
      :timeout="timeout"
      color="error"
      multi-line>
      {{ $lang.labels.maxDeliveryDisWarning }}
      <v-btn
        flat
        @click="snackbar = false"
      >
        {{$lang.actions.close}}
      </v-btn>
    </v-snackbar>
    <v-dialog v-if="validateModal" v-model="validateModal" max-width="1200">
      <validate-modal :companyId="companyId" @close="validateModal = false"/>
    </v-dialog>
    <v-dialog v-if="createCorporationDialog" v-model="createCorporationDialog" max-width="500">
      <confirmation-modal :text="'Create Corporation?'" @submit="createCorporation()" @close="createCorporationDialog = false"/>
    </v-dialog>
    <v-dialog scrollable :fullscreen="$vuetify.breakpoint.smAndDown" v-model="addressDialog" v-if="addressDialog"
              max-width="600px">
      <address-modal @close="addressDialog = false" :country="company.address.country" :address="company.address" :postal="company.address.postalcode" @submit="submitAddress"/>
    </v-dialog>
  </v-flex>
</template>

<script>
  import {DateTime} from 'luxon';

  export default {
    components: {
      validateModal: () => import('../modals/Validate'),
      ConfirmationModal: () => import('../modals/ConfirmationModal'),
      addressModal: () => import('../modals/AddressModal'),
    },
    data() {
      return {
        showCompanies: false,
        isLocalAdmin: false,
        latitudeRule: [
          v => /^(\+|-)?(?:90(?:(?:\.0{1,6})?)|(?:[0-9]|[1-8][0-9])(?:(?:\.[0-9]{1,20})?))$/.test(v) || this.$lang.errors.notValid
        ],
        addressDialog: false,
        distanceRule: [v => /^[0-9]+(\.[0-9]+)?$/.test(v) || this.$lang.errors.missingMaxDist],
        printingConfigurations: [],
        headers: [
          { text: this.$lang.labels.sourceSystem, value: 'sourceSystem', align: 'left' },
          { text: this.$lang.labels.orderType, value: 'orderType', align: 'left' },
          { text: this.$lang.labels.printType, value: 'printType', align: 'left' },
          { text: this.$lang.labels.autoPrinting, value: 'autoPrinting', align: 'left' }
        ],
        validateModal: false,
        companyId: null,
        timeRule: [
          v => /^([01]\d|2[0-3]):?([0-5]\d)$/.test(v) || this.$lang.errors.validTime
        ],
        emailRules: [
          v => /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || this.$lang.errors.validEmail
        ],
        days: [
          {text: this.$lang.labels.sunday, value:'SUNDAY'},
          {text: this.$lang.labels.monday, value:'MONDAY'},
          {text: this.$lang.labels.tuesday, value:'TUESDAY'},
          {text: this.$lang.labels.wednesday, value:'WEDNESDAY'},
          {text: this.$lang.labels.thursday, value:'THURSDAY'},
          {text: this.$lang.labels.friday, value:'FRIDAY'},
          {text: this.$lang.labels.saturday, value:'SATURDAY'},
          {text: this.$lang.labels.publicHoliday, value:'PUBLICHOLIDAY'},
        ],
        openingTimes: [],
        tempOpening: {
          day: '',
          start: '',
          end: '',
        },
        editingHours: false,
        validHours: false,

        vatRates: [],
        tempVat: {
          vat: '',
        },
        editingVat: false,

        miles: [],
        tempMiles: {
          fromMiles: '',
          toMiles: '',
          charges: '',
        },
        defaultCharges: '',
        editingMiles: false,

        amounts: [],
        tempAmount: {
          fromAmount: '',
          toMilse: '',
          charges: '',
        },
        editingAmount: false,
        valid: false,
        isEdit: false,
        payments: JSON.parse(localStorage.payments),
        paymentGateways: JSON.parse(localStorage.paymentGateways),
        languages: JSON.parse(localStorage.languages),
        align: 'left',
        chargeTypes: JSON.parse(localStorage.chargeTypes),
        serviceTypes: JSON.parse(localStorage.serviceTypes),
        allowedVatRates: JSON.parse(localStorage.allowedVatRates),
        company: {
          tableSwitchCode: null,
          oosId: null,
          name: '',
          email: '',
          phone: '',
          currency: '',
          config_update_frequency_in_minutes: 0,
          paymentTypes: [],
          tipTaxRate: null,
          defaultLanguage: '',
          cuisines: [],
          maxDeliveryDistance: null,
          deliveryPostalCodes: '',
          deliveryChargesTaxRate: null,
          paymentIntegration: 'none',
          enableContactForm: false,
          dailyOrdersCounterEnabled: false,
          persistKitchenPrint: false,
          completePrint: false,
          test: false,
          enableReservationForm: false,
          tableIdleTimeInMinutes: 5,
          pricingGroups: [],
          deliveryCharges: {
            chargesByMiles: [],
            chargesByInvoiceAmt: [],
            defaultCharges: null,
            type: '',
            chargesPerMile: null,
          },
          serviceCharges: {
            type: 'noservicecharge',
            taxRate: null,
            percOfTotal: 0,
          },
          minPersons: null,
          maxPersons: null,
          address: {
            line1: '',
            line2: '',
            line3: '',
            line4: '',
            locality: '',
            townCity: '',
            country: '',
            postalCode: '',
            latitude: '',
            longitude: '',
          },
          isDEPEnabled: false,
          contactServerSettings: {
            key: '',
          },
          walletServerSettings: {
            key: '',
          },
          addressServerSettings: {
            key: '',
          },
          depServerSettings: {
            key: '',
          },
          inventoryServerSettings: {
            key: '',
          },
          reservationServerSettings: {
            key: '',
          },
          distanceUnit: '',
          registrationNumber: '',
          registrationLocation: '',
          timezone: '',
        },
        timezones: [],
        bookKeeperEmails: '',
        err: '',
        success: '',
        currencies: ['EUR', 'GBP'],
        language: this.$cookie.get('lang') || 'en',
        cuisines: [],
        statuses: [
          {
            text: this.$lang.labels.active,
            value: 'active',
          },
          {
            text: this.$lang.labels.inactive,
            value: 'inactive',
          },
        ],
        createdOn: '',
        modifiedOn: '',
        allPricingGroups: [],
        snackbar: false,
        timeout: 0,
        noTax: true,
        noTipTax: true,
        noDeliveryTax: true,
        noServiceTax: true,
        countries: ['AT', 'UK'],
        createCorporationDialog: false,
        corporation: {
          companies: [],
          name: null,
          contactServerSettings: {
            enabled: false,
            serverUrl: '',
          },
          addressServerSettings: {
            enabled: false,
            serverUrl: '',
          },
          walletServerSettings: {
            enabled: false,
            serverUrl: '',
          },
          depServerSettings: {
            enabled: false,
            serverUrl: '',
          },
          inventoryServerSettings: {
            enabled: false,
            serverUrl: '',
          },
          authServerSettings:
            {
              enabled: false,
              serverUrl: ''
            },
          reservationServerSettings:
            {
              enabled: false,
              serverUrl: ''
            },
          primaryCompanyId: '',
          dailyOrderCounterPrefixes: {},
        },
        distanceUnits: [],
        selectedCompany: null,
        companies: [],
      };
    },
    watch: {
      selectedCompany(val) {
        if (val) this.fetchSingleCompany(val, false);
      },
      noServiceTax(val) {
        if (val === true) this.company.serviceCharges.taxRate = null;
      },
      noDeliveryTax(val) {
        if (val === true) this.company.deliveryChargesTaxRate = null;
      },
      noTipTax(val) {
        if (val === true) this.company.tipTaxRate = null;
      },
      noTax(val) {
        if (val) {
          this.vatRates.splice(0, this.vatRates.length);
          this.editingVat = true;
          this.noServiceTax = true;
          this.noDeliveryTax = true;
          this.noTipTax = true;
        } else {
          this.editingVat = false;
          if (this.vatRates.length === 0) {
            this.noServiceTax = true;
            this.noDeliveryTax = true;
            this.noTipTax = true;
          }
        }
      },
      err(val) {
        if (val !== '') {
          setTimeout(() => { this.err = ''; }, 5000);
        }
      },
      'company.address.country': function (val) {
        if (val === 'UK') this.company.isDEPEnabled = false;
      },
      'company.isDEPEnabled': function (val) {
        if (!val) this.company.depServerSettings.key = '';
      },
      'company.deliveryCharges.type': function (newVal, oldVal) {
        immediate: false;
        deep: true;
        if (oldVal === '') {
          return;
        }
        if (newVal) {
          this.defaultCharges = '';
          this.miles = [];
          this.editingMiles = false;
          this.amounts = [];
          this.editingAmount = false;
        }
      },
      editingHours(val) {
        if (val === true) {
          this.valid = false;
        }
      },
      'company.currency': function (val) {
        this.fetchPricingGroups(val);
      },
    },
    computed: {
      mappedCompanies() {
        return this.companies.map(company => ({
          text: `${company.name} (${company.companyStatus})`,
          value: company.id,
        }));
      },
      chargeTypesFormatted() {
        return this.chargeTypes.map(row => {
          const type = row;
          if (this.company.distanceUnit === 'meters') {
            type.text = type.text.replace('Miles', 'Meters');
            type.text = type.text.replace('Mile', 'Meter');
          }
          return type;
        });
      },
      openingValidation() {
        const isStartValid = this.validateTime(this.tempOpening.start);
        let isEndValid = this.validateTime(this.tempOpening.end);
        if (this.tempOpening.end === '0000') {
          return isEndValid = false;
        }
        return !!this.tempOpening.day && isStartValid && isEndValid;
      },
      mappedGroups() {
        return this.allPricingGroups.map(group => ({
          text: group.name,
          value: group.id,
        }));
      },
    },
    methods: {
      submitAddress(address) {
        this.company.address = {
          line1: address.line1.trim(),
          line2: address.line2.trim(),
          line3: address.line3.trim(),
          line4: address.line4.trim(),
          locality: address.locality.trim(),
          townCity: address.townCity.trim(),
          country: address.country.trim(),
          postalCode: address.postalCode.trim().toUpperCase(),
          latitude: address.latitude.trim(),
          longitude: address.longitude.trim(),
        };
        this.addressDialog = false;
      },
      moveUp(arr, id) {
        const y = id--;
        var origin = arr[id]
        arr.splice(id, 1, arr[y])
        this.$set(arr, y, origin);
      },
      moveDown(arr, id) {
        const y = id++;
        var origin = arr[id]
        arr.splice(id, 1, arr[y])
        this.$set(arr, y, origin);
      },
      fetchPricingGroups(currency) {
        this.$http.post('pricinggroup/getall', {currency})
          .then((res) => {
            if (res.body.status === 'Success') {
              this.allPricingGroups = res.body.pricingGroups;
              return;
            }
            this.err = res.body.statusText;
          }, (err) => {
            this.err = err.body.statusText || err.body.message;
          });
      },
      formatDate(date) {
        return DateTime.fromISO(date).toFormat('yyyy-MM-dd HH:mm:ss');
      },
      validateTime(time) {
        if (!time) return false;
        let valid = true;
        if (
          parseInt(time[0], 10) > 2 ||
          parseInt(time[0], 10) === 2 && parseInt(time[1], 10) > 3 ||
          parseInt(time[2], 10) > 5 ||
          !time[3]
        ) valid = false;
        if (time.length !== 4) valid = false;
        return valid;
      },
      //Opening Hours
      addHours() {
        this.openingTimes.push({
          day: '',
          start: '',
          end: '',
          isEdit: true,
        });
        this.editingHours = true;
        this.$forceUpdate();
      },
      copyOpening(time) {

        if (this.editingHours) {
          this.$set(time, 'day', this.tempOpening.day);
          this.$set(time, 'start', this.formatTime(this.tempOpening.start));
          this.$set(time, 'end', this.formatTime(this.tempOpening.end));
          this.$set(time, 'isEdit', false);
        }
        this.openingTimes.push({
          day: '',
          start: '',
          end: '',
          isEdit: true,
        });
        this.tempOpening = {
          day: time.day,
          start: this.formatTimeEdit(time.start),
          end: this.formatTimeEdit(time.end),
        };
        this.editingHours = true;
      },
      editOpening(time) {
        this.tempOpening = {
          day: time.day,
          start: this.formatTimeEdit(time.start),
          end: this.formatTimeEdit(time.end),
        };
        this.$set(time, 'isEdit', true);
        this.editingHours = true;
      },

      deleteOpening(index) {
        this.openingTimes.splice(index, 1);
        this.$forceUpdate();
      },

      formatTime(time) {
        const save = time.toString().replace(':', '');
        return `${ save.slice(0, 2) }:${ save.slice(2) }`;
      },
      formatTimeEdit(time) {
        return time.replace(':', '');
      },
      saveOpening(time) {
        this.$set(time, 'day', this.tempOpening.day);
        this.$set(time, 'start', this.formatTime(this.tempOpening.start));
        this.$set(time, 'end', this.formatTime(this.tempOpening.end));
        this.closeOpening(time);
      },

      closeOpening(time) {
        this.tempOpening = {
          day: '',
          start: '',
          end: '',
        };
        this.$set(time, 'isEdit', false);
        this.editingHours = false;
      },

      //Vat
      addVat() {
        this.vatRates.push({
          vat: '',
          isEdit: true,
        });
        this.editingVat = true;
        this.$forceUpdate();
      },
      editVat(vat) {
        this.tempVat = {
          vat: vat.vat,
        };
        this.$set(vat, 'isEdit', true);
        this.editingVat = true;
      },

      deleteVat(index) {
        this.vatRates.splice(index, 1);
        this.$forceUpdate();
      },

      saveVatEdit(vat) {
        this.$set(vat, 'vat', this.tempVat.vat);
        this.closeVatEdit(vat);
      },

      closeVatEdit(vat) {
        this.tempVat = {
          vat: '',
        };
        this.$set(vat, 'isEdit', false);
        this.editingVat = false;
      },

      //Charge by Miles
      addMiles() {
        this.miles.push({
          fromMiles: '',
          toMiles: '',
          charges: '',
          isEdit: true,
        });
        this.editingMiles = true;
        this.$forceUpdate();
      },
      editMiles(charge) {
        this.tempMiles = {
          fromMiles: charge.fromMiles,
          toMiles: charge.toMiles,
          charges: charge.charges,
        };
        this.$set(charge, 'isEdit', true);
        this.editingMiles = true;
      },

      deleteMiles(index) {
        this.miles.splice(index, 1);
        this.$forceUpdate();
      },

      saveMilesEdit(charge) {
        this.$set(charge, 'fromMiles', this.tempMiles.fromMiles);
        this.$set(charge, 'toMiles', this.tempMiles.toMiles);
        this.$set(charge, 'charges', this.tempMiles.charges);
        this.closeMilesEdit(charge);
      },

      closeMilesEdit(charge) {
        this.tempMiles = {
          fromMiles: '',
          toMiles: '',
          charges: '',
        };
        this.$set(charge, 'isEdit', false);
        this.editingMiles = false;
      },

      //Charge by Amount
      addAmount() {
        this.amounts.push({
          fromAmount: '',
          toAmount: '',
          charges: '',
          isEdit: true,
        });
        this.editingAmount = true;
        this.$forceUpdate();
      },
      editAmount(amount) {
        this.tempAmount = {
          fromAmount: amount.fromAmount,
          toAmount: amount.toAmount,
          charges: amount.charges,
        };
        this.$set(amount, 'isEdit', true);
        this.editingAmount = true;
      },

      deleteAmount(index) {
        this.amounts.splice(index, 1);
        this.$forceUpdate();
      },

      saveAmountEdit(amount) {
        this.$set(amount, 'fromAmount', this.tempAmount.fromAmount);
        this.$set(amount, 'toAmount', this.tempAmount.toAmount);
        this.$set(amount, 'charges', this.tempAmount.charges);
        this.closeAmountEdit(amount);
      },

      closeAmountEdit(amount) {
        this.tempAmount = {
          fromAmount: '',
          toAmount: '',
          charges: '',
        };
        this.$set(amount, 'isEdit', false);
        this.editingAmount = false;
      },

      init() {
        return new Promise((resolve) => {
          this.languages = this.languages.sort((a,b) => (a.text > b.text) ? 1 : ((b.text > a.text) ? -1 : 0));
          this.$http.get('system/currencies')
            .then((res) => {
              if (res.body.status === 'Success') {
                this.currencies = res.body.currencies;
                return this.$http.get('system/cuisines');
              }
              throw new Error(res.body.statusText);
            })
            .then((res) => {
              if (res.body.status === 'Success') {
                this.cuisines = res.body.cuisines.sort();
                resolve();
                return
              }
              throw new Error(res.body.statusText);
            })
            .catch((err) => {
              this.err = err.message || err.body.message;
              resolve();
            });
          this.$http.get('system/printingconfigurations/allowedcombinations')
            .then((res) => {
              resolve()
              if (res.body.status === 'Success') {
                this.printingConfigurations = res.body.allowedPrintingCombinations;
                return;
              }
              this.err = res.body.statusText;
            }, (err) => {
              resolve()
              this.err = err.body.statusText || err.body.message;
            });
        });
      },
      validate() {
        this.companyId = this.$route.params.id;
        this.validateModal = true;
      },
      checkEmail(email) {
        return /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email);
      },
      submit(action) {
        const company = {...this.company};
        if (this.bookKeeperEmails !== '') {
          let emailSplit = this.bookKeeperEmails.split(',');
          emailSplit = emailSplit.map(email => {
            email = email.trim();
            email = email.toLowerCase();
            if (!this.checkEmail(email)) return false;
            return email;
          });
          if (emailSplit.includes(false)) {
            this.err = `${this.$lang.errors.wrongEmail}`;
            return;
          }
          Object.assign(company, {bookKeeperEmails: emailSplit});
        }
        if (this.openingTimes.length > 0) {
          const openingDaysAndHours = this.openingTimes.map(time => ({
            day: time.day,
            start: time.start,
            end: time.end,
          }));
          Object.assign(company, {openingDaysAndHours});
        }
        if (!this.noTax && this.vatRates.length > 0) {
          Object.assign(company, {vatRates: this.vatRates.map(rate => rate.vat)});
        } else {
          Object.assign(company, {vatRates: null});
        }
        if (company.vatRates !== null) {
          company.vatRates = company.vatRates.map(vat => Number(vat));
        }
        if (company.address.country === 'AT') {
          if (company.vatRates === null) {
            this.err = this.$lang.errors.noVatRates;
            return;
          } else {
            const arr = company.vatRates.filter(rate => !this.allowedVatRates.at.includes(Number(rate)));
            if (company.address.country === 'AT' && arr.length > 0) {
              this.err = `${this.$lang.errors.wrongVAT}: ${this.allowedVatRates.at}`;
              return;
            }
          }
        }
        if (company.address.country === 'UK') {
          const arr = company.vatRates !== null ? company.vatRates.filter(rate => !this.allowedVatRates.uk.includes(Number(rate))) : [];
          if (arr.length > 0) {
            this.err = `${this.$lang.errors.wrongVatUK}: ${this.allowedVatRates.uk}`;
            return;
          }
        }
        company.address.postalCode = company.address.postalCode.trim().toUpperCase();
        this.company.address.postalCode = company.address.postalCode;
        if (this.company.deliveryPostalCodes) {
          const deliveryPostalCodes = this.company.deliveryPostalCodes.split(',');
          Object.assign(company, {deliveryPostalCodes});
        } else {
          delete company.deliveryPostalCodes;
        }
        if (company.deliveryCharges.type === 'chargesbymiles') {
          Object.assign(company, {
            deliveryCharges: {
              type: this.company.deliveryCharges.type,
              chargesByMiles: this.miles,
            }
          });
        } else {
          delete company.deliveryCharges.chargesByMiles;
        }
        if (company.deliveryCharges.type === 'chargesbyinvoiceamount') {
          Object.assign(company, {
            deliveryCharges: {
              type: this.company.deliveryCharges.type,
              chargesByInvoiceAmt: this.amounts,
            }
          });
        } else {
          delete company.deliveryCharges.chargesByInvoiceAmt;
        }
        if (this.defaultCharges) {
          Object.assign(company.deliveryCharges, {
            defaultCharges: this.defaultCharges,
          });
        } else {
          delete company.deliveryCharges.defaultCharges;
        }
        if (company.deliveryCharges.type === 'chargespermile') {
          Object.assign(company, {
            deliveryCharges: {
              type: this.company.deliveryCharges.type,
              chargesPerMile: this.company.deliveryCharges.chargesPerMile,
            }
          });
        }
        if (company.deliveryCharges.type !== 'chargespermile') {
          delete company.deliveryCharges.chargesPerMile;
        }
        if (company.enableReservationForm === false) {
          delete company.reservationFormDetails;
        }
        let url = this.isEdit ? `company/update/${ this.$route.params.id }` : 'company/create';
        if (action === 'pushConfig') {
          url = `company/addallconfigurations/${ this.$route.params.id }`
        }
        Object.assign(company, {printingConfigurations: this.printingConfigurations});
        if (!company.contactServerSettings.key && company.contactServerSettings.key.length < 1) {
          delete company.contactServerSettings;
        }
        if (!company.walletServerSettings.key && company.walletServerSettings.key.length < 1) {
          delete company.walletServerSettings;
        }
        if (!company.addressServerSettings.key && company.addressServerSettings.key.length < 1) {
          delete company.addressServerSettings;
        }
        if (!company.depServerSettings.key && company.depServerSettings.key.length < 1 || !company.isDEPEnabled) {
          delete company.depServerSettings;
        }
        if (!company.inventoryServerSettings.key && company.inventoryServerSettings.key.length < 1) {
          delete company.inventoryServerSettings;
        }
        if (!company.reservationServerSettings.key && company.reservationServerSettings.key.length < 1) {
          delete company.reservationServerSettings;
        }
        this.$http.post(url, company)
          .then((res) => {
            if (res.body.status === 'Success') {
              if (company.maxDeliveryDistance == 0 || !company.maxDeliveryDistance) {
                this.snackbar = true;
              }
              this.success = this.isEdit ? 'Company updated successfuly' : 'Company created successfuly';
              if (!this.isEdit) {
                this.corporation.name = 'Corporation of ' + this.company.name;
                this.corporation.companies.push(res.body.companyId);
                this.corporation.primaryCompanyId = res.body.companyId;
                this.createCorporationDialog = true;
              }
              setTimeout(() => {
                if (this.isEdit) {
                  this.success = '';
                }
              }, 3000);
              return;
            }
            this.err = res.body.statusText;
          }, (err) => {
            this.err = err.body.statusText || err.body.message;
          });
      },
      createCorporation() {
        this.createCorporationDialog = false;
        let url = 'corporation/create';
        this.$http.post(url, this.corporation)
          .then((res) => {
            if (res.body.status === 'Success') {
              this.success = 'Corporation created successfully';
              setTimeout(() => {
                if (this.isEdit) {
                  this.success = '';
                }
              }, 3000);
              return;
            }
            this.err = res.body.statusText;
          }, (err) => {
            this.err = err.body.statusText || err.body.message;
          });
      },
      fetchAllCompanies() {
        if (this.showCompanies === true) {
          this.showCompanies = false;
          return;
        }
        this.$http.get('company')
          .then((res) => {
            if (res.body.status === 'Success') {
              this.companies = res.body.companies;
              this.showCompanies = true;
              return;
            }
            this.err = res.body.statusText;
          }, (err) => {
            this.err = err.body.statusText || err.body.message;
          });
      },
      fetchSingleCompany(id, isFromStart) {
        this.$http.get(`company/${id}`)
          .then((res) => {
            if (res.body.status === 'Success') {
              const company = res.body.company;
              if (!isFromStart) delete company.depServerSettings;
              if (company.openingDaysAndHours) {
                this.openingTimes = company.openingDaysAndHours.map(current => Object.assign({}, current, {isEdit: false}));
              }
              if (company.vatRates) {
                this.noTax = false;
                this.vatRates = (company.vatRates || []).map(vat => ({
                  vat: vat,
                  isEdit: false,
                }));
              }
              if (company.deliveryCharges) {
                if (company.deliveryCharges.chargesByMiles) {
                  this.miles = company.deliveryCharges.chargesByMiles.map(current => Object.assign({}, current, {isEdit: false}));
                }
                if (company.deliveryCharges.defaultCharges) {
                  this.defaultCharges = company.deliveryCharges.defaultCharges;
                }
                if (company.deliveryCharges.chargesByInvoiceAmt) {
                  company.deliveryCharges.chargesByInvoiceAmt.forEach((current) => {
                    this.amounts.push(Object.assign({}, current, {isEdit: false}));
                  });
                }
              }
              if (!company.serviceCharges) {
                Object.assign(company, {serviceCharges: { type: 'noservicecharge' }});
              }
              if(company.deliveryPostalCodes) {
                company.deliveryPostalCodes = company.deliveryPostalCodes.join(',');
              } else {
                this.$set(company, 'deliveryPostalCodes', '');
              }
              if (!company.cuisines) {
                Object.assign(company, { cuisines: [] });
              }
              if (company.paymentTypes && company.paymentTypes.length > 0) {
                company.paymentTypes = company.paymentTypes.map(current => current.toLowerCase());
              }
              if (company.enableReservationForm === true) {
                Object.assign(company, { minPersons: company.reservationFormDetails.minPersons });
                Object.assign(company, { maxPersons: company.reservationFormDetails.maxPersons });
              }
              this.createdOn = this.formatDate(company.createdOn);
              if (company.modifiedOn) {
                this.modifiedOn = this.formatDate(company.modifiedOn);
              }
              if (company.printingConfigurations) {
                this.printingConfigurations = this.printingConfigurations.map(defaultConfig => {
                  const config = company.printingConfigurations.find(companyConfig =>
                    defaultConfig.orderType === companyConfig.orderType &&
                    defaultConfig.printType === companyConfig.printType &&
                    defaultConfig.sourceSystem === companyConfig.sourceSystem);
                  return config ? defaultConfig = {
                    ...defaultConfig,
                    autoPrinting: company.address.country === 'AT' && defaultConfig.printType === 'invoice' ? false : config.autoPrinting,
                  } : {...defaultConfig};
                });
              }
              if (company.tipTaxRate || company.tipTaxRate === 0) {
                this.noTipTax = false;
              }
              if (company.deliveryChargesTaxRate || company.deliveryChargesTaxRate === 0) {
                this.noDeliveryTax = false;
              }
              if (company.serviceCharges.taxRate || company.serviceCharges.taxRate === 0) {
                this.noServiceTax = false;
              }
              if (this.company.address.country !== 'AT' &&  this.company.address.country !== 'UK') {
                this.company.address.country = '';
              }
              if (!company.contactServerSettings) {
                Object.assign(company, {contactServerSettings: {key: ''}},)
              }
              if (!company.addressServerSettings) {
                Object.assign(company, {addressServerSettings: {key: ''}},)
              }
              if (!company.walletServerSettings) {
                Object.assign(company, {walletServerSettings: {key: ''}},)
              }
              if (!company.depServerSettings) {
                Object.assign(company, {depServerSettings: {key: ''}},)
              }
              if (!company.inventoryServerSettings) {
                Object.assign(company, {inventoryServerSettings: {key: ''}},)
              }
              if (!company.reservationServerSettings) {
                Object.assign(company, {reservationServerSettings: {key: ''}},)
              }
              if (localStorage.distanceunits) {
                this.distanceUnits = JSON.parse(localStorage.distanceunits);
              }
              if (company.bookKeeperEmails && company.bookKeeperEmails.length > 0) {
                this.bookKeeperEmails = company.bookKeeperEmails.join();
              }
              this.company = company;
              return;
            }
            this.err = res.body.statusText
          }, (err) => {
            this.err = err.body.statusText || err.body.message;
          })
      },
    },
    created() {
      const roles = JSON.parse(this.$cookie.get('roles') || '[]');
      this.isLocalAdmin = roles.includes('ROLE_SUN_LOCAL_ADMIN');
      this.init()
        .then(() => {
          if (localStorage.timezones) this.timezones = JSON.parse(localStorage.timezones).sort();
          if (this.$route.params.id) {
            this.isEdit = true;
            this.fetchSingleCompany(this.$route.params.id, true);
            } else {
            if (localStorage.distanceunits) {
              this.distanceUnits = JSON.parse(localStorage.distanceunits);
            } else {
              this.distanceUnits = ['meters', 'miles'];
            }
          }
        })
    },
  };
</script>
<style lang="scss">
  .required {
    .v-label:after {
      content: ' *';
      font-size: inherit;
      color: #f4511e !important;
    }
  }
  .v-messages {
    color: red !important;
    font-weight: bold;
    transform: translate(0, -3px);
  }
</style>
<style lang="scss" scoped>
  .radio-input {
    padding-top: 4px;
    p {
      padding-bottom: 0;
      margin-bottom: 0;
      color: rgba(0,0,0,.54);
    }
    .input-group {
      padding-top: 0 !important;
    }
  }
  .currency-wrap {
    padding-right: 16px;
  }
  .custom-table {
    max-width: 99.9%;

    .table {
      width: calc(100% - 1px);
    }
    .th-adj {
      margin-bottom: -15px;
      font-size: 16px;
    }
    table {
      th {
        padding: 6px 12px !important;
      }
      thead {
        tr {
          height: 42px !important;
          border-bottom: none !important;
        }
      }
    }
    tbody {
      tr {
        &:nth-of-type(odd){
          background-color: #f9f9f9;
        }
      }
      td {
        padding: 0 12px !important;
        border: 1px solid rgba(0, 0, 0, 0.2);
      }
    }
  }
  .id-for-s3 {
    color: rgba(0,0,0,.54);
    font-size: 16px;
  }
  @media only screen and (max-width: 960px) {
    .currency-wrap {
      padding-right: 0;
    }
  }
  .move-row {
    padding: 0 12px !important;
    width: 30px !important;
  }
</style>



// WEBPACK FOOTER //
// CompanyDetails.vue?b6240d66